'use client';

import { appendHrefQuery, buildRecommendationWidgetUrl, linkToString } from '@/helper/urls';
import { useUserStore } from '@/providers/UserStoreProvider';
import type { MultilinkStoryblok } from '@/types/component-types-sb';
import { useEffect, useMemo, useState } from 'react';

export type UseRecommendationWidgetRuntimeParams = {
  _uid: string;
  button_container_id?: string;
  widget_url: string;
  t_mc: string;
  t_art_number: string;
  redirect_url: MultilinkStoryblok | string;
};

export function useRecommendationWidgetRuntime({
  _uid,
  button_container_id,
  widget_url,
  t_mc,
  t_art_number,
  redirect_url,
}: UseRecommendationWidgetRuntimeParams) {
  const { isRedirecting, loggingIn } = useUserStore((state) => ({
    isRedirecting: state.isRedirecting,
    loggingIn: state.loggingIn,
  }));
  const [loading, setLoading] = useState(true);
  const containerId = button_container_id?.trim() || _uid;
  const baseRedirectUrl = useMemo(() => linkToString(redirect_url), [redirect_url]);
  const [redirectUrl, setRedirectUrl] = useState(baseRedirectUrl);
  const [tmc, setTmc] = useState(t_mc);

  useEffect(() => {
    if (!loggingIn && !isRedirecting) {
      setLoading(false);
    }
  }, [loggingIn, isRedirecting]);

  useEffect(() => {
    let enhancedUrl = baseRedirectUrl;

    if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
      try {
        const currentPathname = window.location.pathname;
        const initialPath = sessionStorage.getItem('initialPath');

        if (initialPath) {
          const initialPathname = initialPath.split('?')[0];

          if (initialPathname === currentPathname) {
            const searchParams = new URLSearchParams(window.location.search);
            const sessionTmc = sessionStorage.getItem('t_mc') || searchParams.get('t_mc');
            if (sessionTmc) {
              setTmc(sessionTmc);
            }

            const swdVkn = sessionStorage.getItem('swd_vkn') || searchParams.get('swd_vkn');
            const swdCampaign = sessionStorage.getItem('swd_campaign') || searchParams.get('swd_campaign');
            if (swdVkn || swdCampaign) {
              const params = new URLSearchParams();
              if (swdVkn) params.set('swd_vkn', swdVkn);
              if (swdCampaign) params.set('swd_campaign', swdCampaign);

              enhancedUrl = appendHrefQuery(enhancedUrl, params.toString());
            }
          }
        }
      } catch (e) {
        console.error('Error enhancing RecommendationWidget redirectUrl', e);
      }
    }
    setRedirectUrl(enhancedUrl);
  }, [baseRedirectUrl, t_mc]);

  const widgetSrc = buildRecommendationWidgetUrl(widget_url, containerId, tmc, t_art_number, redirectUrl);

  const handleCtaClick = () => {
    const container = document.getElementById(containerId);
    if (!container) return;

    const button =
      container.querySelector('button') || container.querySelector('a') || container.querySelector('[role="button"]');
    if (button instanceof HTMLElement) {
      button.click();
    }
  };

  return {
    loading,
    containerId,
    widgetSrc,
    handleCtaClick,
  };
}
